import React, {PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createRefetchContainer, graphql, RelayRefetchProp} from 'react-relay';
import {
	Breadcrumbs,
	BulkSelector,
	FlexColumn,
	FlexRow,
	Heading,
	Icon,
	Table,
	Text,
	useTableFunctions,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import Styled from 'styled-components';
import type {ExpenseManagementPage_viewer$data as ExpenseManagementPage_viewer} from './__generated__/ExpenseManagementPage_viewer.graphql';
import {getNotNullishNodes} from '../../forecast-app/shared/util/NotNullPredicate';
import {trackEvent, trackPage} from '../../tracking/amplitude/TrackingV2';
import {isDateInLockedPeriod} from '../../components/new-ui/project/project-budget-v3/util/PeriodLocks';
import PredefinedDateRangePicker, {DatePickerOnChange} from './PredefinedDateRangePicker';
import {dateObjectToIsoDate, getPreviousMonthRange} from '../../forecast-app/shared/util/DateUtil';
import ExpenseManagementFilterButton, {getInitialExpenseManagementFilters} from './ExpenseManagementFilterButton';
import {getFilterFunctions} from '../../forecast-app/shared/components/filters/filter_logic.js';
import {ExpenseManagementTotals} from './ExpenseManagementTotals';
import ExpenseManagementChangeViewButton, {getExpenseManagementEyeOptions} from './ExpenseManagementChangeViewButton';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {startOfMonth} from 'date-fns';
import Util from '../../forecast-app/shared/util/util';
import ExpenseManagementExportButton from './ExpenseManagementExportButton';
import {useEyeOptions} from '../../forecast-app/shared/hooks/useEyeOptions';
import {EXPENSE_MANAGEMENT_COLUMNS} from './Constants';
import {downloadExpenseFiles} from './ExpenseManagementUtil';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ExpenseItem, ExpenseRow} from './ExpenseTypes';
import moment from 'moment';
import useMeasureRender from '../../forecast-app/shared/hooks/useMeasureRender';
import {profilePicRectSrc} from '../../directApi';

const GROUP_BY_LOCALSTORAGE_KEY = 'expense-management-group-by';
const PROJECT_CURRENCY_LOCALSTORAGE_KEY = 'expense-management-project-currency';
const EYE_OPTIONS_LOCALSTORAGE_KEY = 'expense-management-eye-options';
const DATE_RANGE_LOCALSTORAGE_KEY = 'expense-management-date-range';

export const getDefaultDateRange = (): DatePickerOnChange => {
	const storedDateRange = Util.localStorageGetItem(DATE_RANGE_LOCALSTORAGE_KEY);

	if (storedDateRange) {
		return JSON.parse(storedDateRange);
	}

	const date = new Date();
	const range = getPreviousMonthRange(date);
	return {startDate: dateObjectToIsoDate(range?.startDate), endDate: dateObjectToIsoDate(range?.endDate)};
};

const PageWrapper = Styled.div<{buyNowTime?: string}>`
  padding: ${({buyNowTime}) => (buyNowTime ? '64px' : '32px')};
`;
const BreadcrumbsWrapper = Styled.div`
  padding-bottom: 24px;
 `;

const Toolbar = ({children}: PropsWithChildren<unknown>) => <FlexRow>{children}</FlexRow>;
Toolbar.Left = ({children}: PropsWithChildren<unknown>) => <FlexRow gap={'s'}>{children}</FlexRow>;
Toolbar.Right = ({children}: PropsWithChildren<unknown>) => (
	<FlexRow gap={'s'} justifyContent={'end'}>
		{children}
	</FlexRow>
);

interface ExpenseManagementPageProps {
	viewer: ExpenseManagementPage_viewer;
	relay: RelayRefetchProp;
	buyNowTime?: string;
}

const getExpenseDate = (expense: ExpenseItem) =>
	new Date(expense.expenseYear || 0, (expense.expenseMonth || 0) - 1, expense.expenseDay || 0);

export const isLocked = (expense: ExpenseItem): boolean => {
	const lockedPeriods = getNotNullishNodes(expense.project?.fixedPriceLocks?.edges).filter(period => period.locked);
	return isDateInLockedPeriod(moment(getExpenseDate(expense)), lockedPeriods);
};

const PAGE_NAME = 'Expense Management';

const ExpenseManagementPage = ({viewer, relay: {refetch}, buyNowTime}: ExpenseManagementPageProps) => {
	const {formatMessage} = useIntl();
	const scrollRef = useRef(null);

	useEffect(() => {
		document.title = 'Finance - Expenses';
		trackPage(PAGE_NAME);
	}, []);

	const loadUseProjectCurrency = () => {
		const useProjectCurrencyOption = Util.localStorageGetItem(PROJECT_CURRENCY_LOCALSTORAGE_KEY) === 'true';
		trackEvent('Use Project Currency Option', 'Loaded', {
			value: useProjectCurrencyOption,
		});
		return useProjectCurrencyOption;
	};

	const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
	const [startDate, setStartDate] = useState<string | null>(defaultDateRange.startDate);
	const [endDate, setEndDate] = useState<string | null>(defaultDateRange.endDate);

	const [groupings, setGroupings] = useState<string[]>(() =>
		JSON.parse(Util.localStorageGetItem(GROUP_BY_LOCALSTORAGE_KEY) || '[]')
	);
	const [filterFunctions, setFilterFunctions] = useState(() => getFilterFunctions(getInitialExpenseManagementFilters()));
	const [useProjectCurrency, setUseProjectCurrency] = useState(() => loadUseProjectCurrency());

	const [tableRef, table] = useTableFunctions();

	const onGroupingsChange = (groupings: string[]) => {
		Util.localStorageSetItem(GROUP_BY_LOCALSTORAGE_KEY, JSON.stringify(groupings));
		setGroupings(groupings);

		const groupingObj = groupings.reduce((result, item, index) => {
			result[`level_${index + 1}`] = item;
			return result;
		}, {});

		trackEvent('Group By', 'Changed', {
			groupBy: groupings,
			...groupingObj,
		});
	};
	const hasDeliverables = hasFeatureFlag('deliverables');
	const [eyeOptionMap, setEyeOptionMap] = useState({});
	const [, , eyeOptions, handleEyeOptionsChange] = useEyeOptions(
		getExpenseManagementEyeOptions(),
		EYE_OPTIONS_LOCALSTORAGE_KEY,
		undefined,
		setEyeOptionMap
	);

	const onUseProjectCurrencyChange = checked => {
		Util.localStorageSetItem(PROJECT_CURRENCY_LOCALSTORAGE_KEY, checked);
		trackEvent('Use Project Currency', checked ? 'Selected' : 'Deselected');
		setUseProjectCurrency(checked);
	};

	const billableText = formatMessage({id: 'expense_item_modal.billable'});
	const nonBillableText = formatMessage({id: 'expense_item_modal.non_billable'});
	const approvedText = formatMessage({id: 'common.approved'});
	const unapprovedText = formatMessage({id: 'common.unapproved'});
	const companyCurrency = viewer.company?.currency || '';

	const onDatePickerChange = (onChangeEvent: DatePickerOnChange) => {
		setStartDate(onChangeEvent.startDate);
		setEndDate(onChangeEvent.endDate);
		refetch(onChangeEvent);

		Util.localStorageSetItem(DATE_RANGE_LOCALSTORAGE_KEY, JSON.stringify(onChangeEvent));
		trackEvent('Date Picker Dates', 'Changed', {
			startDate: onChangeEvent.startDate,
			endDate: onChangeEvent.endDate,
		});
	};

	const filterByFilters = useCallback(
		(expense: ExpenseItem) => filterFunctions.expenseFilter(expense) && filterFunctions.projectFilter(expense.project),
		[filterFunctions]
	);

	const expenses = useMemo(() => {
		const mapExpenseToRow = (expense: ExpenseItem): ExpenseRow => {
			const expenseDate = getExpenseDate(expense);
			const cost = useProjectCurrency ? expense.cost : expense.costBaseCurrency;
			const price = useProjectCurrency ? expense.price : expense.priceBaseCurrency;
			return {
				...expense,
				totalCost: (expense.quantity || 0) * (cost || 0),
				totalPrice: expense.billable ? (expense.quantity || 0) * (price || 0) : undefined,
				billingOption: expense.billable ? billableText : nonBillableText,
				markup: expense.billable && cost ? Math.round((((price || 0) - cost) / cost) * 100) / 100 : undefined,
				expenseDate,
				status: expense.approved ? approvedText : unapprovedText,
				attachedFiles: getNotNullishNodes(expense.files?.edges).map(file => file.name || '') || [],
				locked: expense.invoiced || isLocked(expense),
				month: startOfMonth(expenseDate),
				readOnly: !!expense.project?.readOnlyAccess,
			};
		};

		return getNotNullishNodes(viewer?.company?.expenseItems?.edges).filter(filterByFilters).map(mapExpenseToRow);
	}, [viewer, filterByFilters, useProjectCurrency]);
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
	useMeasureRender(PAGE_NAME, 'Rendered', [expenses]);

	const selectedRows = useMemo(
		() => expenses.filter(expense => selectedRowIds.includes(expense.id)),
		[selectedRowIds, expenses]
	);

	const numLockedRows = useMemo(() => selectedRows.filter(row => row.locked).length, [selectedRows]);

	const totalFilesToDownload = useMemo(
		() => selectedRows.reduce((total, expense) => total + getNotNullishNodes(expense.files?.edges).length, 0),
		[selectedRows]
	);

	const hideBulkAction = numLockedRows > 0 && numLockedRows === selectedRows.length;

	const onSelectionChange = useCallback((rows: ExpenseRow[]) => setSelectedRowIds(rows.map(row => row.id)), []);

	const groupingColumnHeader = groupings.length
		? formatMessage({id: 'expense_management.grouping'}) +
		  ': ' +
		  groupings.map(grouping => formatMessage({id: 'expense_management.column.' + grouping})).join(' / ')
		: formatMessage({id: 'expense_management.expense_name'});
	const showPersonAvatar = groupings.includes(EXPENSE_MANAGEMENT_COLUMNS.PERSON);

	const initialSorting = [
		{id: EXPENSE_MANAGEMENT_COLUMNS.DATE, desc: true},
		{id: EXPENSE_MANAGEMENT_COLUMNS.MONTH, desc: true},
	];

	const handleItemDelete = (row: ExpenseRow) => {
		showModal({
			type: MODAL_TYPE.EXPENSE_MANAGEMENT_DELETE_EXPENSE,
			selectedRow: row,
			companyId: viewer.company?.id,
		});
	};

	const openExpenseModal = (expense: ExpenseRow, clickedOn: string, openFilesTab?: boolean) => {
		showModal({
			type: MODAL_TYPE.EXPENSE_ITEM,
			expenseItemId: expense.id,
			projectId: expense.project ? expense.project.id : null,
			locked: expense.locked,
			readOnly: expense.readOnly,
			selectedTab: openFilesTab ? 'files' : undefined,
			onDateChange: () => refetch({startDate, endDate}),
		});

		trackEvent('Expense Modal By Clicked On ' + clickedOn, 'Viewed', {expenseId: expense.id});
	};

	const selectedLockedRows = useMemo(() => selectedRows.filter(item => item.locked), [selectedRows]);

	const selectedlockedRowIds = useMemo(() => selectedLockedRows.map(row => row.id), [selectedLockedRows]);

	const handleBulkItemDelete = () => {
		if (selectedRows.length < 1) return;
		showModal({
			type: MODAL_TYPE.EXPENSE_MANAGEMENT_DELETE_EXPENSES,
			selectedRows,
			companyId: viewer.company?.id,
			onSuccessfulDelete: () => {
				table.setSelections(selectedlockedRowIds);
			},
		});
	};

	const openChangeProjectPhaseModal = () => {
		showModal({
			type: MODAL_TYPE.EXPENSE_ITEM_CHANGE_PROJECT_PHASE_MODAL,
			expenseItems: selectedRows,
		});
	};

	const openChangeApprovalModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_EXPENSE_APPROVAL,
			expenseItems: selectedRows,
		});
	};

	const openChangeBillableStatusModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_EXPENSE_BILLABLE_STATUS,
			expenseItems: selectedRows,
		});
	};

	const openChangeFixedPriceBillingModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_EXPENSE_FIXED_PRICE_BILLING,
			expenseItems: selectedRows,
		});
	};
	const openChangeMarkupModal = () => {
		showModal({
			type: MODAL_TYPE.EXPENSE_MANAGEMENT_CHANGE_MARKUP,
			selectedRows,
		});
	};

	const openChangeExpenseCategoryModal = () => {
		showModal({
			type: MODAL_TYPE.CHANGE_EXPENSE_CATEGORY,
			viewer: viewer,
			selectedRows,
		});
	};

	const openChangePersonModal = () => {
		showModal({
			type: MODAL_TYPE.EXPENSE_ITEM_CHANGE_PERSON_MODAL,
			selectedRows,
		});
	};

	const rowCurrency = row => (useProjectCurrency && row.project.rateCard ? row.project.rateCard.currency : companyCurrency);

	return (
		<CustomScrollDiv
			setScrollbarsRef={node => {
				scrollRef.current = node;
			}}
		>
			<PageWrapper buyNowTime={buyNowTime}>
				<BreadcrumbsWrapper>
					<Breadcrumbs>
						<Breadcrumbs.Item isDisabled>
							<FormattedMessage id="settings_finance.menuTitle" />
						</Breadcrumbs.Item>
						<Breadcrumbs.Item>
							<FormattedMessage id="expense_management.title" />
						</Breadcrumbs.Item>
					</Breadcrumbs>
				</BreadcrumbsWrapper>
				<FlexColumn gap={'l'}>
					<FlexRow gap={'s'}>
						<Heading size={'3'}>
							<FormattedMessage id="expense_management.title" />
						</Heading>
					</FlexRow>
					<Toolbar>
						<Toolbar.Left>
							<PredefinedDateRangePicker startDate={startDate} endDate={endDate} onChange={onDatePickerChange} />
						</Toolbar.Left>
						<Toolbar.Right>
							<ExpenseManagementFilterButton viewer={viewer} setFilterFunctions={setFilterFunctions} />
							<ExpenseManagementChangeViewButton
								groupings={groupings}
								onGroupingsChange={onGroupingsChange}
								useProjectCurrency={useProjectCurrency}
								onUseProjectCurrencyChange={onUseProjectCurrencyChange}
								eyeOptions={eyeOptions}
								onEyeOptionsChange={handleEyeOptionsChange}
							/>
							<ExpenseManagementExportButton
								exportData={table.exportData}
								includeColumns={[EXPENSE_MANAGEMENT_COLUMNS.NAME, ...groupings]}
							/>
						</Toolbar.Right>
					</Toolbar>
					<ExpenseManagementTotals expenses={expenses} currency={companyCurrency} />
					<Heading size={'4'}>
						<FormattedMessage id="expense_management.expense_list" />
					</Heading>
					<Table
						data-cy={'expenses-table'}
						data={expenses}
						grouping={groupings}
						width={'100%'}
						scrollRef={scrollRef}
						initialSorting={initialSorting}
						stickyHeader
						tableRef={tableRef}
						enableRowSelection={row => !row.original.readOnly}
						onSelectionChange={onSelectionChange}
						getRowId={(row: ExpenseRow) => row.id}
					>
						<Table.GroupingColumn header={groupingColumnHeader} defaultColumnKey={'name'} allowSorting>
							<Table.TextColumn
								id={EXPENSE_MANAGEMENT_COLUMNS.NAME}
								accessorKey={'name'}
								header={formatMessage({id: 'expense_management.expense_name'})}
								allowSorting
								ellipsis
								icon={row => row.locked && <Icon icon={'lockClosed'} />}
								alignIconRight
								onClick={(row: ExpenseRow) => () => openExpenseModal(row, 'Expense Name')}
							/>
							<Table.TextColumn
								id={EXPENSE_MANAGEMENT_COLUMNS.CLIENT}
								accessorKey={'project.client.name'}
								data-cy={'client-name'}
								defaultGroupingValue={formatMessage({id: 'common.no_client'})}
								header={formatMessage({id: 'common.client'})}
							/>
							<Table.DateColumn
								id={EXPENSE_MANAGEMENT_COLUMNS.MONTH}
								accessorKey={'month'}
								data-cy={'month'}
								header={formatMessage({id: 'common.month'})}
								formatOptions={{month: 'short', year: 'numeric'}}
							/>
						</Table.GroupingColumn>
						<Table.ColoredIdIconColumn
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.PROJECT]}
							accessorKey={'project.customProjectId'}
							data-cy={'project-id'}
							header={'P-ID'}
							exportFormatter={value => value}
							renderProps={row => ({
								color: row.project.projectColor,
								text: row.project.customProjectId,
							})}
						/>
						<Table.TextColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.PROJECT}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.PROJECT]}
							accessorKey={'project.name'}
							data-cy={'project-name'}
							header={formatMessage({id: 'common.project'})}
							allowSorting
							ellipsis
						/>
						<Table.TextColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.PHASE}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.PHASE]}
							accessorKey={'phase.name'}
							data-cy={'phase'}
							header={formatMessage({id: 'expense_management.column.phase'})}
							allowSorting
							ellipsis
						/>
						{hasDeliverables ? (
							<Table.TextColumn
								id={EXPENSE_MANAGEMENT_COLUMNS.DELIVERABLE}
								visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.DELIVERABLE]}
								accessorKey={'deliverable.name'}
								data-cy={'deliverable'}
								header={formatMessage({id: 'expense_management.column.deliverable'})}
								allowSorting
								ellipsis
							/>
						) : undefined}
						<Table.TextColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.CATEGORY}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.CATEGORY]}
							accessorKey={'category.name'}
							data-cy={'category'}
							header={formatMessage({id: 'insights.category'})}
							allowSorting
							ellipsis
						/>
						<Table.CurrencyColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.TOTAL_COST}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.TOTAL_COST]}
							accessorKey={'totalCost'}
							data-cy={'total-cost'}
							header={formatMessage({id: 'expense_item_modal.total_cost'})}
							rowCurrency={rowCurrency}
							allowSorting
						/>
						<Table.TextColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.BILLING_OPTION}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.BILLING_OPTION]}
							accessorKey={'billingOption'}
							header={formatMessage({id: 'expense_management.billing_option'})}
							allowSorting
						/>
						<Table.CurrencyColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.TOTAL_PRICE}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.TOTAL_PRICE]}
							accessorKey={'totalPrice'}
							data-cy={'total-price'}
							header={formatMessage({id: 'expense_item_modal.total_price'})}
							rowCurrency={rowCurrency}
							allowSorting
						/>
						<Table.PercentageColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.MARKUP}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.MARKUP]}
							accessorKey={'markup'}
							data-cy={'markup'}
							header={formatMessage({id: 'common.markup'})}
							allowSorting
						/>
						{showPersonAvatar ? (
							<Table.AvatarColumn
								id={EXPENSE_MANAGEMENT_COLUMNS.PERSON}
								visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.PERSON]}
								accessorKey={'person.fullName'}
								groupingValue={row => row.person?.id}
								image={row =>
									row.person?.profilePictureId ? profilePicRectSrc(row.person?.profilePictureId) : undefined
								}
								defaultGroupingValue={formatMessage({id: 'common.no_person'})}
								data-cy={'person'}
								header={formatMessage({id: 'common.person'})}
							/>
						) : (
							<Table.TextColumn
								id={EXPENSE_MANAGEMENT_COLUMNS.PERSON}
								visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.PERSON]}
								accessorKey={'person.fullName'}
								data-cy={'person'}
								header={formatMessage({id: 'common.person'})}
								allowSorting
								ellipsis
							/>
						)}
						<Table.DateColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.DATE}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.DATE]}
							accessorKey={'expenseDate'}
							data-cy={'date'}
							header={formatMessage({id: 'common.date'})}
							allowSorting
						/>
						<Table.TextColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.APPROVAL_STATUS}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.APPROVAL_STATUS]}
							accessorKey={'status'}
							data-cy={'status'}
							header={formatMessage({id: 'common.status'})}
							icon={row => row.approved && <Icon icon={'checkCircle'} color={'success'} />}
							alignIconRight
							allowSorting
						/>
						<Table.ButtonColumn
							id={EXPENSE_MANAGEMENT_COLUMNS.FILES}
							visible={eyeOptionMap[EXPENSE_MANAGEMENT_COLUMNS.FILES]}
							data-cy={'attached-files'}
							header={formatMessage({id: 'common.files'})}
							button={row => ({
								label: row.attachedFiles.length,
								onClick: () => openExpenseModal(row, 'Files Icon', true),
								icon: 'paperclip',
								tooltip: (
									<FlexColumn>
										{row.attachedFiles.map((fileName: string, index: number) => (
											<Text key={index}>{fileName}</Text>
										))}
									</FlexColumn>
								),
							})}
						/>
						<Table.ActionColumn header={formatMessage({id: 'common.actions'})}>
							<Table.ActionColumn.Option
								data-cy={'edit'}
								onClick={row => openExpenseModal(row, 'Edit Expense')}
								hidden={row => row.readOnly}
							>
								<FormattedMessage id={'expense_management.edit_expense'} />
							</Table.ActionColumn.Option>
							<Table.ActionColumn.Option
								data-cy={'view'}
								onClick={row => openExpenseModal(row, 'View Expense')}
								hidden={row => !row.readOnly}
							>
								<FormattedMessage id={'expense_management.view_expense'} />
							</Table.ActionColumn.Option>
							<Table.ActionColumn.Option
								data-cy={'download-files'}
								onClick={row => downloadExpenseFiles([row])}
								hidden={row => row.files.edges.length === 0}
							>
								<FormattedMessage id={'expense_management.download_files'} />
							</Table.ActionColumn.Option>
							<Table.ActionColumn.Option
								data-cy={'delete'}
								onClick={handleItemDelete}
								disabled={row => row.locked || row.readOnly}
							>
								<FormattedMessage id={'expense_management.delete_expense'} />
							</Table.ActionColumn.Option>
						</Table.ActionColumn>
						{useProjectCurrency ? (
							<Table.ExportOnlyColumn header={formatMessage({id: 'common.currency'})} value={rowCurrency} />
						) : undefined}
					</Table>
				</FlexColumn>
			</PageWrapper>
			<BulkSelector
				label={formatMessage({id: 'expense_management.bulk_selector_label'}, {count: selectedRows.length})}
				onClear={table.clearSelections}
				show={selectedRows.length > 0}
				data-cy={'bulk-selector'}
				bottomOffset={buyNowTime ? 40 : undefined}
			>
				<BulkSelector.MainAction onClick={openChangeExpenseCategoryModal} data-cy={'bulk-change-expense-category'}>
					{formatMessage({id: 'expense_management.change_category'})}
				</BulkSelector.MainAction>
				<BulkSelector.MainAction onClick={openChangeApprovalModal} data-cy={'change-approval-status'}>
					{formatMessage({id: 'expense_management.change_approval'})}
				</BulkSelector.MainAction>
				<BulkSelector.MainAction
					onClick={() => downloadExpenseFiles(selectedRows, true)}
					data-cy={'bulk-download-expenses'}
					disabled={totalFilesToDownload < 1}
				>
					{formatMessage({id: 'expense_management.download_all_files'})} ({totalFilesToDownload})
				</BulkSelector.MainAction>
				<BulkSelector.ExtraAction onClick={openChangePersonModal} data-cy={'action-change-project-person'}>
					<FormattedMessage id={'expense_management.actions.change_person'} />
				</BulkSelector.ExtraAction>
				<BulkSelector.ExtraAction onClick={openChangeProjectPhaseModal} data-cy={'action-change-project-phase'}>
					<FormattedMessage id={'expense_management.actions.change_project_phase'} />
				</BulkSelector.ExtraAction>
				<BulkSelector.ExtraAction
					onClick={handleBulkItemDelete}
					data-cy={'bulk-expense-item-delete'}
					hidden={hideBulkAction}
				>
					{formatMessage({id: 'expense_management.delete_expenses'})}
				</BulkSelector.ExtraAction>
				<BulkSelector.ExtraAction
					onClick={openChangeFixedPriceBillingModal}
					data-cy={'bulk-change-fixed-price-billing'}
				>
					<FormattedMessage id={'expense_management.actions.change_fixed_price_billing'} />
				</BulkSelector.ExtraAction>
				<BulkSelector.ExtraAction onClick={openChangeBillableStatusModal} data-cy={'change-billable-status'}>
					{formatMessage({id: 'expense_management.mark_as_billable_label'})}
				</BulkSelector.ExtraAction>
				<BulkSelector.ExtraAction onClick={openChangeMarkupModal} data-cy={'bulk-change-markup'}>
					<FormattedMessage id={'expense_management.actions.change_markup'} />
				</BulkSelector.ExtraAction>
			</BulkSelector>
		</CustomScrollDiv>
	);
};

export default createRefetchContainer(
	ExpenseManagementPage,
	{
		viewer: graphql`
			fragment ExpenseManagementPage_viewer on Viewer
			@argumentDefinitions(startDate: {type: "String"}, endDate: {type: "String"}) {
				id
				...ExpenseManagementFilterButton_viewer
				company {
					id
					currency
					expenseCategories(first: 10000) @connection(key: "Company_expenseCategories", filters: []) {
						edges {
							node {
								id
								name
								disabled
							}
						}
					}
					expenseItems(first: 100000, startDate: $startDate, endDate: $endDate)
						@connection(key: "Company_expenseItems", filters: []) {
						edges {
							node {
								id
								name
								cost
								costBaseCurrency
								price
								priceBaseCurrency
								quantity
								approved
								billable
								partOfFixedPrice
								invoiced
								expenseYear
								expenseMonth
								expenseDay
								files(first: 1000) @connection(key: "ExpenseItem_files") {
									edges {
										node {
											id
											name
										}
									}
								}
								person {
									id
									fullName
									profilePictureId
								}
								project {
									id
									name
									projectColor
									companyProjectId
									customProjectId
									currentProjectStatus {
										color
									}
									client {
										id
										name
									}
									budgetType
									defaultPeriodBudgetType
									projectPersons(first: 1000, contactsOnly: true) {
										edges {
											node {
												isContactPerson
												person {
													id
												}
											}
										}
									}
									rateCard {
										id
										currency
									}
									fullAccessToProject
									readOnlyAccess
									fixedPriceLocks(first: 1000) @connection(key: "Project_fixedPriceLocks", filters: []) {
										edges {
											node {
												id
												startDate
												endDate
												locked
											}
										}
									}
								}
								category {
									id
									name
								}
								phase {
									id
									name
								}
								deliverable {
									id
									name
								}
							}
						}
					}
				}
			}
		`,
	},
	graphql`
		query ExpenseManagementPageRefetchQuery($startDate: String, $endDate: String) {
			viewer {
				component(name: "expense_management")
				...ExpenseManagementPage_viewer @arguments(startDate: $startDate, endDate: $endDate)
			}
		}
	`
);
